import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, change as changeFieldValue, formValueSelector } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    Button, Card, CardBody, CardText, CardTitle, Col, FormGroup, Input, InputGroup, InputGroupAddon, Label, Row,
} from 'reactstrap';
import { DateTimePicker, HelpInfoAlert, PricingsField, RolesField, SalepointsField, TextArrayField, TickettypesField } from '../../components';
import CardIconAction from '../CardIconAction/CardIconAction';
import RedundantOverridesAlert from '../RedundantOverridesAlert';
import { EMPTY_OBJECT } from '../../services/stableEmpty';
import Utils from '../../services/Utils';
import { getActivePricingConstraints, hasTemporalConstraint, omit_constraint_keys } from '../../services/pricingConstraints';
import { categories_is_notable } from '../../services/pricingCategories';
import { find_redundant_constraint_overrides } from '../../services/redundantOverrides';
import { usePlacesSeatCategorySuggestions } from '../../hooks';
import { loadTickettypes, clearPricingRulesOverride } from '../../redux/modules/tickettypes';

import './TickettypeConstraintsForm.css';

const FORM_NAME = 'tickettypeForm';
const formSelector = formValueSelector(FORM_NAME);
const all_pricing_roles = Utils.pricingRoles();
const all_pricing_roles_length = all_pricing_roles.length;

const DEFAULT_CONSTRAINTS = [
    { id: 'temporal', label: 'pricinglists.pricing.constraint_temporal' },
    { id: 'roles', label: 'pricinglists.pricing.constraint_roles' },
    { id: 'salepoints', label: 'pricinglists.pricing.constraint_salepoints' },
    { id: 'tickettypes', label: 'pricinglists.pricing.constraint_tickettypes' },
    { id: 'categories', label: 'pricinglists.pricing.constraint_categories' },
];

const OVERRIDE_CONSTRAINTS = [
    { id: 'temporal', label: 'pricinglists.pricing.constraint_temporal' },
    { id: 'roles', label: 'pricinglists.pricing.constraint_roles' },
    { id: 'salepoints', label: 'pricinglists.pricing.constraint_salepoints' },
    { id: 'tickettypes', label: 'pricinglists.pricing.constraint_tickettypes' },
    { id: 'categories', label: 'pricinglists.pricing.constraint_categories' },
    { id: 'hides_pricings', label: 'pricinglists.pricing.constraint_hides_pricings' },
];

const pricing_has_sellers_limit = (sellers) => Array.isArray(sellers);

const pricing_has_meaningful_constraints = (pricing) => {
    const normalized = pricing?.rules || {};
    return (
        hasTemporalConstraint(normalized)
        || pricing_has_sellers_limit(normalized.only_for_roles)
        || categories_is_notable(pricing)
        || Array.isArray(normalized.only_for_salepoints)
        || Array.isArray(normalized.only_for_tickettypes)
        || Array.isArray(normalized.exclude_tickettypes)
        || Array.isArray(normalized.hides_pricings_while_available)
    );
};

const get_initial_override_constraints = (pricing) => (
    getActivePricingConstraints(
        pricing?.rules,
        all_pricing_roles_length
    )
);

const get_initial_default_constraints = (rules) => {
    const normalized = rules || {};
    const constraints = [];
    if (hasTemporalConstraint(normalized))
        constraints.push('temporal');
    // Present key (including []) = active constraint — same as pricing overrides.
    if (Array.isArray(normalized.only_for_roles))
        constraints.push('roles');
    if (Array.isArray(normalized.only_for_salepoints))
        constraints.push('salepoints');
    if (
        Array.isArray(normalized.only_for_tickettypes)
        || Array.isArray(normalized.exclude_tickettypes)
    )
        constraints.push('tickettypes');
    if ('only_for_categories' in normalized)
        constraints.push('categories');
    return [...new Set(constraints)];
};

const ConstraintPicker = ({
    availableConstraints,
    constraintToAdd,
    setConstraintToAdd,
    onAdd,
}) => {
    const { t } = useTranslation();
    return (
        <Row className="mb-3">
            <Col sm={8}>
                <Input
                    type="select"
                    value={constraintToAdd}
                    onChange={e => setConstraintToAdd(e.target.value)}
                    disabled={availableConstraints.length === 0}
                >
                    <option value="">
                        {availableConstraints.length === 0
                            ? t('pricinglists.pricing.all_constraints_added')
                            : t('pricinglists.pricing.constraint_placeholder')}
                    </option>
                    {availableConstraints.map(c => (
                        <option key={c.id} value={c.id}>{t(c.label)}</option>
                    ))}
                </Input>
            </Col>
            <Col sm={4}>
                <Button
                    color="info"
                    onClick={onAdd}
                    disabled={availableConstraints.length === 0 || !constraintToAdd}
                    block
                >
                    <i className="fa fa-plus"></i>&nbsp;{t('pricinglists.pricing.add_constraint')}
                </Button>
            </Col>
        </Row>
    );
};

const remove_button = (t, constraintId, onRemove) => (
    <span className="CardIconActions CardIconActions--inline CardIconActions--inline-start ConstraintRemoveAction mb-0">
        <CardIconAction
            id={`remove-tt-constraint-${constraintId}`}
            icon="fa-trash"
            danger
            title={t('pricinglists.pricing.remove_constraint')}
            onClick={() => onRemove(constraintId)}
        />
    </span>
);

const DefaultConstraintsCard = ({ salepoints, tickettypes }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const seatCategorySuggestions = usePlacesSeatCategorySuggestions();
    const formRules = useSelector(state => formSelector(state, 'rules') || EMPTY_OBJECT);

    const [activeConstraints, setActiveConstraints] = useState(
        () => get_initial_default_constraints(formRules)
    );
    const [constraintToAdd, setConstraintToAdd] = useState('');
    const tickettypeId = useSelector(state => formSelector(state, '_id'));

    const change_rules_field = useCallback((field, value) => {
        dispatch(changeFieldValue(FORM_NAME, `rules.${field}`, value));
    }, [dispatch]);

    useEffect(() => {
        setActiveConstraints(get_initial_default_constraints(formRules));
        setConstraintToAdd('');
    // Only reset when switching ticket types.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tickettypeId]);

    useEffect(() => {
        if (!constraintToAdd)
            return;
        const available = DEFAULT_CONSTRAINTS.filter(c => !activeConstraints.includes(c.id));
        if (!available.find(c => c.id === constraintToAdd))
            setConstraintToAdd('');
    }, [activeConstraints, constraintToAdd]);

    const addConstraint = () => {
        if (!constraintToAdd || activeConstraints.includes(constraintToAdd))
            return;

        if (constraintToAdd === 'roles')
            change_rules_field('only_for_roles', []);
        if (constraintToAdd === 'categories')
            change_rules_field('only_for_categories', []);
        if (constraintToAdd === 'tickettypes') {
            change_rules_field('only_for_tickettypes', []);
            change_rules_field('exclude_tickettypes', []);
        }
        if (constraintToAdd === 'salepoints')
            change_rules_field('only_for_salepoints', []);

        setActiveConstraints([...activeConstraints, constraintToAdd]);
        setConstraintToAdd('');
    };

    const removeConstraint = (constraintId) => {
        // Replace parent `rules` — change(path, undefined) is a no-op when the
        // field exists in initialValues (redux-form shouldDelete guard).
        // Read latest rules from the store: a stale selector snapshot would keep
        // only_for_roles (or other keys) and the constraint would come back on save.
        dispatch((formDispatch, getState) => {
            const latest_rules = formSelector(getState(), 'rules') || EMPTY_OBJECT;
            formDispatch(changeFieldValue(
                FORM_NAME,
                'rules',
                omit_constraint_keys(latest_rules, constraintId)
            ));
        });
        setActiveConstraints(activeConstraints.filter(c => c !== constraintId));
    };

    const availableConstraints = DEFAULT_CONSTRAINTS.filter(c => !activeConstraints.includes(c.id));

    return (
        <Card className="CardShell mb-3">
            <CardBody>
                <CardTitle>
                    <i className="fa fa-lock"></i>&nbsp;
                    {t('tickettypes.constraints.default_title')}
                </CardTitle>
                <CardText tag="div">
                    <ConstraintPicker
                        availableConstraints={availableConstraints}
                        constraintToAdd={constraintToAdd}
                        setConstraintToAdd={setConstraintToAdd}
                        onAdd={addConstraint}
                    />

                    {activeConstraints.includes('roles') && (
                        <div className="border rounded p-2 mb-2">
                            <FormGroup row className="mb-0">
                                <Label tag="div" sm={12} className="d-flex align-items-center justify-content-start text-left mb-2">
                                    {remove_button(t, 'roles', removeConstraint)}
                                    {t('tickettypes.constraints.only_for_roles')}
                                </Label>
                                <Col sm={12}>
                                    <RolesField
                                        useBucketRoles
                                        name="rules.only_for_roles"
                                        placeholder={t('tickettypes.constraints.all_roles_placeholder')}
                                    />
                                </Col>
                            </FormGroup>
                        </div>
                    )}

                    {activeConstraints.includes('temporal') && (
                        <div className="border rounded p-2 mb-2">
                            <FormGroup row>
                                <Label tag="div" sm={12} className="d-flex align-items-center justify-content-start text-left mb-2">
                                    {remove_button(t, 'temporal', removeConstraint)}
                                    {t('pricinglists.pricing.constraint_temporal')}
                                </Label>
                            </FormGroup>
                            <Row>
                                <Col sm={6}>
                                    <FormGroup row className="mb-0">
                                        <Label sm={12}>{t('pricinglists.pricing.not_before')}</Label>
                                        <Col sm={12}>
                                            <Field
                                                component={DateTimePicker}
                                                type="text"
                                                name="rules.not_before"
                                                maxBoundField="rules.not_after"
                                            />
                                        </Col>
                                    </FormGroup>
                                </Col>
                                <Col sm={6}>
                                    <FormGroup row className="mb-0">
                                        <Label sm={12}>{t('pricinglists.pricing.not_after')}</Label>
                                        <Col sm={12}>
                                            <Field
                                                component={DateTimePicker}
                                                type="text"
                                                name="rules.not_after"
                                                endOfDayIfMidnight
                                                minBoundField="rules.not_before"
                                            />
                                        </Col>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {activeConstraints.includes('tickettypes') && (
                        <div className="border rounded p-2 mb-2">
                            <FormGroup row>
                                <Label tag="div" sm={12} className="d-flex align-items-center justify-content-start text-left mb-2">
                                    {remove_button(t, 'tickettypes', removeConstraint)}
                                    {t('pricinglists.pricing.constraint_tickettypes')}
                                </Label>
                            </FormGroup>
                            <Row>
                                <Col sm={6}>
                                    <FormGroup row className="mb-0">
                                        <Label sm={12}>{t('pricinglists.pricing.limit_to_tickettypes')}</Label>
                                        <Col sm={12}>
                                            <TickettypesField
                                                name="rules.only_for_tickettypes"
                                                tickettypes={tickettypes}
                                            />
                                        </Col>
                                    </FormGroup>
                                </Col>
                                <Col sm={6}>
                                    <FormGroup row className="mb-0">
                                        <Label sm={12}>{t('pricinglists.pricing.exclude_tickettypes')}</Label>
                                        <Col sm={12}>
                                            <TickettypesField
                                                name="rules.exclude_tickettypes"
                                                tickettypes={tickettypes}
                                            />
                                        </Col>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {activeConstraints.includes('salepoints') && (
                        <div className="border rounded p-2 mb-2">
                            <FormGroup row className="mb-0">
                                <Label tag="div" sm={12} className="d-flex align-items-center justify-content-start text-left mb-2">
                                    {remove_button(t, 'salepoints', removeConstraint)}
                                    {t('tickettypes.constraints.only_for_salepoints')}
                                </Label>
                                <Col sm={12}>
                                    <SalepointsField
                                        name="rules.only_for_salepoints"
                                        salepoints={salepoints}
                                    />
                                </Col>
                            </FormGroup>
                        </div>
                    )}

                    {activeConstraints.includes('categories') && (
                        <div className="border rounded p-2 mb-2">
                            <FormGroup row className="mb-0">
                                <Label tag="div" sm={12} className="d-flex align-items-center justify-content-start text-left mb-2">
                                    {remove_button(t, 'categories', removeConstraint)}
                                    {t('pricinglists.pricing.categories')}
                                </Label>
                                <Col sm={12}>
                                    <TextArrayField
                                        name="rules.only_for_categories"
                                        suggestions={seatCategorySuggestions}
                                    />
                                </Col>
                            </FormGroup>
                        </div>
                    )}
                </CardText>
            </CardBody>
        </Card>
    );
};

const PricingOverrideConstraints = ({ pricingId, pricing, salepoints, tickettypes, allPricingOptions }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const seatCategorySuggestions = usePlacesSeatCategorySuggestions();

    const [activeConstraints, setActiveConstraints] = useState(
        () => get_initial_override_constraints(pricing)
    );
    const [constraintToAdd, setConstraintToAdd] = useState('');

    const change_pricing_field = useCallback((field, value) => {
        dispatch(changeFieldValue(FORM_NAME, `pricings.${pricingId}.${field}`, value));
    }, [dispatch, pricingId]);

    useEffect(() => {
        setActiveConstraints(get_initial_override_constraints(pricing));
        setConstraintToAdd('');
    // Only reset when switching override cards; form values may lag behind the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pricingId]);

    useEffect(() => {
        if (!constraintToAdd)
            return;
        const available = OVERRIDE_CONSTRAINTS.filter(c => !activeConstraints.includes(c.id));
        if (!available.find(c => c.id === constraintToAdd))
            setConstraintToAdd('');
    }, [activeConstraints, constraintToAdd]);

    const addConstraint = () => {
        if (!constraintToAdd || activeConstraints.includes(constraintToAdd))
            return;

        if (constraintToAdd === 'roles' && !Array.isArray(pricing?.rules?.only_for_roles))
            change_pricing_field('rules.only_for_roles', []);
        if (constraintToAdd === 'categories')
            change_pricing_field('rules.only_for_categories', []);
        if (constraintToAdd === 'hides_pricings')
            change_pricing_field('rules.hides_pricings_while_available', []);

        setActiveConstraints([...activeConstraints, constraintToAdd]);
        setConstraintToAdd('');
    };

    const removeConstraint = (constraintId) => {
        // Replace parent pricing.rules — change(path, undefined) cannot clear
        // fields that still exist in form initialValues.
        // Missing only_for_roles = inherit tickettype defaults.
        change_pricing_field('rules', omit_constraint_keys(pricing?.rules, constraintId));
        setActiveConstraints(activeConstraints.filter(c => c !== constraintId));
    };

    const availableConstraints = OVERRIDE_CONSTRAINTS.filter(c => !activeConstraints.includes(c.id));

    return (
        <>
            <ConstraintPicker
                availableConstraints={availableConstraints}
                constraintToAdd={constraintToAdd}
                setConstraintToAdd={setConstraintToAdd}
                onAdd={addConstraint}
            />

            {activeConstraints.includes('roles') && (
                <div className="border rounded p-2 mb-2">
                    <FormGroup row className="mb-0">
                        <Label tag="div" sm={12} className="d-flex align-items-center justify-content-start text-left mb-2">
                            {remove_button(t, 'roles', removeConstraint)}
                            {t('pricinglists.pricing.limit_to_sellers')}
                        </Label>
                        <Col sm={12}>
                            <RolesField
                                name={`pricings[${pricingId}].rules.only_for_roles`}
                                placeholder={t('tickettypes.constraints.all_roles_placeholder')}
                            />
                        </Col>
                    </FormGroup>
                </div>
            )}

            {activeConstraints.includes('temporal') && (
                <div className="border rounded p-2 mb-2">
                    <FormGroup row>
                        <Label tag="div" sm={12} className="d-flex align-items-center justify-content-start text-left mb-2">
                            {remove_button(t, 'temporal', removeConstraint)}
                            {t('pricinglists.pricing.constraint_temporal')}
                        </Label>
                    </FormGroup>
                    <Row>
                        <Col sm={6}>
                            <FormGroup row className="mb-0">
                                <Label sm={12}>{t('pricinglists.pricing.not_before')}</Label>
                                <Col sm={12}>
                                    <Field
                                        component={DateTimePicker}
                                        type="text"
                                        name={`pricings[${pricingId}].rules.not_before`}
                                        maxBoundField={`pricings[${pricingId}].rules.not_after`}
                                    />
                                </Col>
                            </FormGroup>
                        </Col>
                        <Col sm={6}>
                            <FormGroup row className="mb-0">
                                <Label sm={12}>{t('pricinglists.pricing.not_after')}</Label>
                                <Col sm={12}>
                                    <Field
                                        component={DateTimePicker}
                                        type="text"
                                        name={`pricings[${pricingId}].rules.not_after`}
                                        endOfDayIfMidnight
                                        minBoundField={`pricings[${pricingId}].rules.not_before`}
                                    />
                                </Col>
                            </FormGroup>
                        </Col>
                    </Row>
                </div>
            )}

            {activeConstraints.includes('tickettypes') && (
                <div className="border rounded p-2 mb-2">
                    <FormGroup row>
                        <Label tag="div" sm={12} className="d-flex align-items-center justify-content-start text-left mb-2">
                            {remove_button(t, 'tickettypes', removeConstraint)}
                            {t('pricinglists.pricing.constraint_tickettypes')}
                        </Label>
                    </FormGroup>
                    <Row>
                        <Col sm={6}>
                            <FormGroup row className="mb-0">
                                <Label sm={12}>{t('pricinglists.pricing.limit_to_tickettypes')}</Label>
                                <Col sm={12}>
                                    <TickettypesField
                                        name={`pricings[${pricingId}].rules.only_for_tickettypes`}
                                        tickettypes={tickettypes}
                                    />
                                </Col>
                                <small className="form-text text-muted">
                                    {t('tickettypes.constraints.only_for_tickettypes_help')}
                                </small>
                            </FormGroup>
                        </Col>
                        <Col sm={6}>
                            <FormGroup row className="mb-0">
                                <Label sm={12}>{t('pricinglists.pricing.exclude_tickettypes')}</Label>
                                <Col sm={12}>
                                    <TickettypesField
                                        name={`pricings[${pricingId}].rules.exclude_tickettypes`}
                                        tickettypes={tickettypes}
                                    />
                                </Col>
                            </FormGroup>
                        </Col>
                    </Row>
                </div>
            )}

            {activeConstraints.includes('salepoints') && (
                <div className="border rounded p-2 mb-2">
                    <FormGroup row className="mb-0">
                        <Label tag="div" sm={12} className="d-flex align-items-center justify-content-start text-left mb-2">
                            {remove_button(t, 'salepoints', removeConstraint)}
                            {t('pricinglists.pricing.limit_to_salepoints')}
                        </Label>
                        <Col sm={12}>
                            <SalepointsField
                                name={`pricings[${pricingId}].rules.only_for_salepoints`}
                                salepoints={salepoints}
                            />
                        </Col>
                    </FormGroup>
                </div>
            )}

            {activeConstraints.includes('categories') && (
                <div className="border rounded p-2 mb-2">
                    <FormGroup row className="mb-0">
                        <Label tag="div" sm={12} className="d-flex align-items-center justify-content-start text-left mb-2">
                            {remove_button(t, 'categories', removeConstraint)}
                            {t('pricinglists.pricing.categories')}
                        </Label>
                        <Col sm={12}>
                            <TextArrayField
                                name={`pricings[${pricingId}].rules.only_for_categories`}
                                suggestions={seatCategorySuggestions}
                            />
                        </Col>
                    </FormGroup>
                </div>
            )}

            {activeConstraints.includes('hides_pricings') && (
                <div className="border rounded p-2 mb-2">
                    <FormGroup row className="mb-0">
                        <Label tag="div" sm={12} className="d-flex align-items-center justify-content-start text-left mb-2">
                            {remove_button(t, 'hides_pricings', removeConstraint)}
                            {t('pricinglists.pricing.constraint_hides_pricings')}
                        </Label>
                        <Col sm={12}>
                            <PricingsField
                                multi
                                name={`pricings[${pricingId}].rules.hides_pricings_while_available`}
                                pricings={allPricingOptions}
                                excludeId={pricingId}
                            />
                            <small className="form-text text-muted">
                                {t('pricinglists.pricing.hides_pricings_while_available_help')}
                            </small>
                        </Col>
                    </FormGroup>
                </div>
            )}
        </>
    );
};

const TickettypeConstraintsForm = ({ tickettype, salepoints, isOneTimePass }) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { tickettypes } = useSelector(state => state.tickettypes);
    const formRules = useSelector(state => formSelector(state, 'rules') || EMPTY_OBJECT);
    const formPricings = useSelector(state => formSelector(state, 'pricings') || EMPTY_OBJECT);
    const [visibleIds, setVisibleIds] = useState([]);
    const [persistedOverrideIds, setPersistedOverrideIds] = useState([]);
    const [pricingIdToAdd, setPricingIdToAdd] = useState('');
    const [pricingSelectKey, setPricingSelectKey] = useState(0);

    useEffect(() => {
        dispatch(loadTickettypes());
    }, [dispatch]);

    useEffect(() => {
        const ids = Object.keys(tickettype?.pricings || {}).filter(
            id => pricing_has_meaningful_constraints(tickettype.pricings[id])
        );
        setVisibleIds(ids);
        setPersistedOverrideIds(ids);
    // Only reset when switching ticket types; live form edits may lag behind the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tickettype?._id]);

    const pricingOptions = useMemo(() => (
        Object.keys(tickettype?.pricings || {})
            .filter(id => !visibleIds.includes(id))
            .map(id => ({ id, name: tickettype.pricings[id].name }))
    ), [tickettype, visibleIds]);

    const pricingName = useCallback((id) => (
        tickettype?.pricings?.[id]
            ? Utils.localized_or_fallback(tickettype.pricings[id].name, i18n.language, id)
            : id
    ), [tickettype, i18n.language]);

    const allPricingOptions = useMemo(() => (
        Object.keys(tickettype?.pricings || {}).map(id => ({
            id,
            name: tickettype.pricings[id].name,
        }))
    ), [tickettype?.pricings]);

    const clearPricingToAdd = useCallback(() => {
        setPricingIdToAdd('');
        setPricingSelectKey(key => key + 1);
    }, []);

    const addOverride = () => {
        if (!pricingIdToAdd || visibleIds.includes(pricingIdToAdd))
            return;
        setVisibleIds([...visibleIds, pricingIdToAdd]);
        clearPricingToAdd();
    };

    const removeOverride = useCallback((id) => {
        // Clear pricing.rules entirely — missing only_for_roles inherits TT defaults.
        dispatch(clearPricingRulesOverride(id));
        dispatch(changeFieldValue(FORM_NAME, `pricings.${id}.rules`, {}));
        // One-way purge of accidental legacy keys still present in the form.
        dispatch(changeFieldValue(FORM_NAME, `pricings.${id}.sellers`, undefined));
        dispatch(changeFieldValue(FORM_NAME, `pricings.${id}.categories`, undefined));
        setVisibleIds(prev => prev.filter(v => v !== id));
        clearPricingToAdd();
    }, [dispatch, clearPricingToAdd]);

    const redundantItems = useMemo(() => {
        if (isOneTimePass)
            return [];
        return find_redundant_constraint_overrides(formPricings, formRules, visibleIds, persistedOverrideIds)
            .map(id => ({ id, label: pricingName(id), icon: 'fa-tag' }));
    }, [formPricings, formRules, visibleIds, persistedOverrideIds, isOneTimePass, pricingName]);

    return (
        <div className="TickettypeConstraintsForm">
            {isOneTimePass ? (
                <div className="mt-5">
                    <HelpInfoAlert helpId="tickettypes.one_time_pass_constraints" dismissable={false}>
                        <Trans
                            i18nKey="tickettypes.renewTypeInformation.one_time_pass_constraints_information"
                            components={{
                                pricinglistsLink: <Link className="alert-link" to="/pricinglists" />,
                            }}
                        />
                    </HelpInfoAlert>
                </div>
            ) : (
                <>
                    <div className="TickettypeConstraintsForm-addOverride mb-3">
                        <RedundantOverridesAlert
                            items={redundantItems}
                            onRemove={removeOverride}
                        />
                    </div>
                    <Row>
                        <Col>
                            <DefaultConstraintsCard salepoints={salepoints} tickettypes={tickettypes} />
                        </Col>
                    </Row>
                    <div className="mb-3">
                        <InputGroup className="w-50">
                            <PricingsField
                                key={pricingSelectKey}
                                pricings={pricingOptions}
                                value={pricingIdToAdd || ''}
                                onChange={setPricingIdToAdd}
                            />
                            <InputGroupAddon addonType="append">
                                <Button color="success" disabled={!pricingIdToAdd} onClick={addOverride}>
                                    <i className="fa fa-plus"></i>&nbsp;
                                    {t('tickettypes.add_override')}
                                </Button>
                            </InputGroupAddon>
                        </InputGroup>
                    </div>
                    <Row>
                        {visibleIds.map(id => (
                            <Col sm={12} key={id} className="mb-3">
                                <Card className="CardShell">
                                    <CardBody>
                                        <CardTitle className="d-flex align-items-center justify-content-start w-100 text-left">
                                            <span className="CardIconActions CardIconActions--inline CardIconActions--inline-start mb-0">
                                                <CardIconAction
                                                    id={`delete-constraint-override-${id}`}
                                                    icon="fa-trash"
                                                    danger
                                                    title={t('tickettypes.constraints.remove_override')}
                                                    onClick={() => removeOverride(id)}
                                                />
                                            </span>
                                            <span>{t('tickettypes.stocks.on_pricing', { pricing: pricingName(id) })}</span>
                                        </CardTitle>
                                        <CardText tag="div">
                                            <PricingOverrideConstraints
                                                pricingId={id}
                                                pricing={formPricings?.[id] || tickettype?.pricings?.[id]}
                                                salepoints={salepoints}
                                                tickettypes={tickettypes}
                                                allPricingOptions={allPricingOptions}
                                            />
                                        </CardText>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </>
            )}
        </div>
    );
};

export default TickettypeConstraintsForm;
